import { compose, createStore, applyMiddleware } from 'redux';
import Raven from 'raven-js';
import createRavenMiddleware from 'raven-for-redux';
import thunk from 'redux-thunk';
import pollTransactions from '@blockcatio/tabby-library/lib/middleware/pollTransactions';
import createPersistenceMiddleware from '@blockcatio/tabby-library/lib/middleware/persistence';
import trackTransactionProgress from '@blockcatio/tabby-library/lib/middleware/trackTransactionProgress';
import pollTokenBalance from '@blockcatio/tabby-library/lib/middleware/pollTokenBalance';
import pollTokenAllowance from '@blockcatio/tabby-library/lib/middleware/pollTokenAllowance';

import reducers from './reducers';
import pollAccount from './middleware/pollAccount';
import pollAccountBalance from './middleware/pollAccountBalance';
import pollPayments from './middleware/pollPayments';
import pollRecommendedGasPrices from './middleware/pollRecommendedGasPrices';
import pollEthCurrencyExchangeRates from './middleware/pollEthCurrencyExchangeRates';
import pollCatEthCurrencyExchangeRate from './middleware/pollCatEthCurrencyExchangeRate';
import createErrorHandler from './middleware/errorHandler';
import updateNetwork from './middleware/updateNetwork';
import updateCatFee from './middleware/updateCatFee';
import updateEthFee from './middleware/updateEthFee';
import currencyCodePersistence from './middleware/currencyCodePersistence';
import ethExchangeRatesPersistence from './middleware/ethExchangeRatesPersistence';
import catEthExchangeRatePersistence from './middleware/catEthExchangeRatePersistence';
import introPersistence from './middleware/introPersistence';
import localPaymentsPersistence from './middleware/localPaymentsPersistence';
import localTokensPersistence from './middleware/localTokensPersistence';
import tosPersistence from './middleware/tosPersistence';
import walletPersistence from './middleware/walletPersistence';
import updateTokenInfo from './middleware/updateTokenInfo';
import updateLatestBlockNumber from './middleware/updateLatestBlockNumber';
import routeTransactions from './middleware/routeTransactions';
import actions from './actions';
import {
  catAllowanceUpdate,
  catAllowanceUpdateFailed,
  catBalanceUpdate,
  catBalanceUpdateFailed,
} from './actions/account';
import {
  tokenAllowanceUpdate,
  tokenAllowanceUpdateFailed,
  tokenBalanceUpdate,
  tokenBalanceUpdateFailed,
} from './actions/token';
import { BALANCE_ALLOWANCE_POLL_FREQUENCY } from './constants';
import { PAYMENT_SEND_SUBMITTED, APP_INIT, APP_SHUTDOWN } from './actionTypes';
import { getAddress } from './selectors/account';
import { getPayContractAddress, getCatTokenContractAddress, getCspcAddress } from './selectors/app';
import { getTokenAddress } from './selectors/token';

// eslint-disable-next-line no-underscore-dangle
const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
export default (services, config) =>
  createStore(
    reducers,
    composeEnhancers(applyMiddleware(
      thunk.withExtraArgument(services),
      createRavenMiddleware(Raven),
      createErrorHandler(services.errorReporter),
      createPersistenceMiddleware(
        [
          currencyCodePersistence,
          ethExchangeRatesPersistence,
          catEthExchangeRatePersistence,
          localPaymentsPersistence,
          localTokensPersistence,
          introPersistence,
          tosPersistence,
          walletPersistence,
        ],
        services,
      ),
      pollAccount(services),
      pollAccountBalance(services),
      pollPayments(services),
      pollTransactions(services, actions),
      pollEthCurrencyExchangeRates(services),
      pollCatEthCurrencyExchangeRate(services),
      pollRecommendedGasPrices(services),
      updateNetwork(services),
      updateCatFee(services),
      updateEthFee(services),
      updateTokenInfo(services),
      updateLatestBlockNumber(services),
      routeTransactions(services),
      trackTransactionProgress(services, config.get('ETHERSCAN_TRANSACTION_URL')),
      // Custom token.
      pollTokenAllowance(services, {
        frequency: BALANCE_ALLOWANCE_POLL_FREQUENCY,
        getOwnerAddress: getAddress,
        getSpenderAddress: getPayContractAddress,
        getTokenAddress,
        tokenAllowanceUpdate,
        tokenAllowanceUpdateFailed,
        checkStart: (action, afterState, prevState) =>
          getTokenAddress(afterState) !== getTokenAddress(prevState),
        checkStop: action => action.type === PAYMENT_SEND_SUBMITTED,
      }),
      // Custom token.
      pollTokenBalance(services, {
        frequency: BALANCE_ALLOWANCE_POLL_FREQUENCY,
        getAddress,
        getTokenAddress,
        tokenBalanceUpdate,
        tokenBalanceUpdateFailed,
        checkStart: (action, afterState, prevState) =>
          getTokenAddress(afterState) !== getTokenAddress(prevState),
        checkStop: action => action.type === PAYMENT_SEND_SUBMITTED,
      }),
      // CAT Token.
      pollTokenAllowance(services, {
        frequency: BALANCE_ALLOWANCE_POLL_FREQUENCY,
        getOwnerAddress: getAddress,
        getSpenderAddress: getCspcAddress,
        getTokenAddress: getCatTokenContractAddress,
        tokenAllowanceUpdate: catAllowanceUpdate,
        tokenAllowanceUpdateFailed: catAllowanceUpdateFailed,
        checkStart: action => action.type === APP_INIT,
        checkStop: action => action.type === APP_SHUTDOWN,
      }),
      // CAT token.
      pollTokenBalance(services, {
        frequency: BALANCE_ALLOWANCE_POLL_FREQUENCY,
        getAddress,
        getTokenAddress: getCatTokenContractAddress,
        tokenBalanceUpdate: catBalanceUpdate,
        tokenBalanceUpdateFailed: catBalanceUpdateFailed,
        checkStart: action => action.type === APP_INIT,
        checkStop: action => action.type === APP_SHUTDOWN,
      }),
    )),
  );



// WEBPACK FOOTER //
// ./src/store.js